import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeConfig } from '../../data/listeners/config';
import { saveConfig } from '../../data/commands/settingsCommands';
import ListEditor from '../../components/ListEditor';

export default function Settings() {
  const { tenantId } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = subscribeConfig(tenantId, setCfg, (e) => setError(e.message));
    return unsub;
  }, [tenantId]);

  if (!cfg) return <div>Loading…</div>;

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setList = (key) => (next) => setCfg((c) => ({ ...c, [key]: next }));
  const setCodes = (kind) => (next) =>
    setCfg((c) => ({
      ...c,
      noteReasonCodes: { ...c.noteReasonCodes, [kind]: next },
    }));

  async function save() {
    setError(''); setMsg(''); setBusy(true);
    try {
      await saveConfig(tenantId, {
        proofPolicy: cfg.proofPolicy,
        requireNextStep: cfg.requireNextStep,
        defaultSortDir: cfg.defaultSortDir,
        tiers: cfg.tiers,
        channels: cfg.channels,
        noteReasonCodes: cfg.noteReasonCodes,
      });
      setMsg('Settings saved.');
    } catch (e) {
      setError(e.message || 'Could not save settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: 620 }}>
      <div className="field">
        <label>Proof of touchpoint</label>
        <select className="input" value={cfg.proofPolicy} onChange={(e) => set({ proofPolicy: e.target.value })}>
          <option value="optional">Optional</option>
          <option value="required">Required (block Log Touchpoint without proof)</option>
        </select>
      </div>

      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={cfg.requireNextStep} onChange={(e) => set({ requireNextStep: e.target.checked })} />
        Require a next step on every touch
      </label>

      <div className="field">
        <label>Default list sort</label>
        <select className="input" value={cfg.defaultSortDir} onChange={(e) => set({ defaultSortDir: e.target.value })}>
          <option value="desc">Highest score first</option>
          <option value="asc">Lowest score first</option>
        </select>
      </div>

      <div className="field">
        <label>Tier options</label>
        <ListEditor items={cfg.tiers ?? []} onChange={setList('tiers')} placeholder="Add a tier…" />
      </div>
      <div className="field">
        <label>Channel options</label>
        <ListEditor items={cfg.channels ?? []} onChange={setList('channels')} placeholder="Add a channel…" />
      </div>

      <div className="field">
        <label>Status reason codes</label>
        <ListEditor items={cfg.noteReasonCodes.Status} onChange={setCodes('Status')} placeholder="Add a status code…" />
      </div>
      <div className="field">
        <label>Action reason codes</label>
        <ListEditor items={cfg.noteReasonCodes.Action} onChange={setCodes('Action')} placeholder="Add an action code…" />
      </div>

      {error && <div className="form-error">{error}</div>}
      {msg && <div style={{ color: 'var(--presto-success)', fontSize: 'var(--text-small)' }}>{msg}</div>}

      <div>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
